import React, { useState, useEffect } from 'react';
import { Info, Upload, Delete as DeleteIcon, Music, Loading, Caution, Voice, Down } from '@icon-park/react';
import { uploadFile, FILE_TYPES } from '../../api/upload';
import { generateVoice, getVoiceSpeakers } from '../../api/voice';
import { formatFileSize } from '../../utils/fileUtils';
import './styles/AudioUploader.css';

function AudioUploader({ data, onUpdate, labelId, labelTitle }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [ttsLoading, setTtsLoading] = useState(false);
  // 优先使用 data.ttsText（从一键语音传入），其次使用音频名称或标签标题
  const [ttsText, setTtsText] = useState(data?.ttsText || (data?.audio?.name) || labelTitle || '语音讲解');

  // 发音人相关状态
  const [speakers, setSpeakers] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [loadingSpeakers, setLoadingSpeakers] = useState(false);

  // 加载发音人列表
  useEffect(() => {
    const loadSpeakers = async () => {
      try {
        setLoadingSpeakers(true);
        const result = await getVoiceSpeakers();
        if (Array.isArray(result)) {
          setSpeakers(result);
          // 设置默认发音人
          const defaultSpeaker = result.find(s => s.isDefault);
          if (defaultSpeaker) {
            setSelectedVoice(defaultSpeaker.code);
          }
        }
      } catch (err) {
        console.warn('加载发音人列表失败:', err);
        // 使用默认发音人
        setSpeakers([
          { code: 'zhitian_emo', name: '知甜（情感女声）', description: '温柔甜美', gender: 'female', isDefault: true },
          { code: 'zhichu_emo', name: '知楚（情感男声）', description: '磁性低沉', gender: 'male', isDefault: false },
        ]);
        setSelectedVoice('zhitian_emo');
      } finally {
        setLoadingSpeakers(false);
      }
    };
    loadSpeakers();
  }, []);

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      console.log(`开始上传音频: ${file.name}, 大小: ${formatFileSize(file.size)}`);

      // 上传音频到OSS/CDN
      const audioUrl = await uploadFile(file, {
        fileType: FILE_TYPES.AUDIO,
        folder: 'labels/audios',
        onProgress: (percent) => {
          setUploadProgress(percent);
          console.log(`音频上传进度: ${percent}%`);
        }
      });

      // 更新数据（只保存URL）
      onUpdate({ 
        ...data, 
        audio: { 
          url: audioUrl,  // ✅ CDN URL
          name: file.name,
          size: file.size,
          sizeFormatted: formatFileSize(file.size)
        } 
      });

      console.log('音频上传成功:', audioUrl);
    } catch (err) {
      console.error('音频上传失败:', err);
      setError(err.message || '音频上传失败，请重试');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteAudio = () => {
    onUpdate({ ...data, audio: null });
  };

  const handleGenerateVoice = async () => {
    if (!ttsText || !ttsText.trim()) {
      setError('请输入要合成的文本');
      return;
    }
    try {
      setError('');
      setTtsLoading(true);
      // 使用独立的语音生成接口（不依赖labelId）
      const voice = await generateVoice({
        text: ttsText.trim(),
        voice: selectedVoice || undefined,
      });
      console.log('语音生成结果:', voice);
      if (voice && voice.fileUrl) {
        const newAudioData = {
          url: voice.fileUrl,
          name: `${voice.voice || 'voice'}.${voice.format || 'mp3'}`,
          size: voice.fileSize,
          sizeFormatted: voice.fileSize ? formatFileSize(voice.fileSize) : ''
        };
        console.log('更新音频数据:', newAudioData);
        onUpdate({
          ...data,
          audio: newAudioData
        });
      } else {
        setError('生成语音失败：未返回文件URL');
      }
    } catch (err) {
      console.error('生成语音失败:', err);
      setError(err.message || '生成语音失败，请确认已登录并有权限');
    } finally {
      setTtsLoading(false);
    }
  };

  // 获取当前选中的发音人信息
  const currentSpeaker = speakers.find(s => s.code === selectedVoice) || speakers[0];

  return (
    <div className="audio-uploader-container">
      <div className="uploader-header">
        <span className="uploader-title">音频样式</span>
        <span className="info-icon"><Info theme="outline" size={14} fill="#999" strokeWidth={3} /></span>
      </div>
      
      <h3 className="section-title">音频添加</h3>
      
      {/* 错误提示 */}
      {error && (
        <div className="upload-error">
          <Caution theme="filled" size={16} fill="#faad14" /> {error}
        </div>
      )}

      {/* 已有音频时显示信息和删除按钮 */}
      {data.audio ? (
        <div className="audio-info-bar">
          <div className="audio-info-left">
            <Music theme="filled" size={18} fill="#2563eb" strokeWidth={3} />
            <span className="audio-name">{data.audio.name}</span>
            {data.audio.sizeFormatted && (
              <span className="audio-size">({data.audio.sizeFormatted})</span>
            )}
          </div>
          <button className="delete-audio-btn" onClick={handleDeleteAudio}>
            <DeleteIcon theme="outline" size={14} fill="#fff" strokeWidth={3} />
            删除
          </button>
        </div>
      ) : (
        <div className="upload-area-audio">
          <input
            type="file"
            id="audio-upload"
            accept="audio/*"
            onChange={handleAudioUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <label
            htmlFor="audio-upload"
            className={`upload-box-audio ${uploading ? 'uploading' : ''}`}
          >
            {uploading ? (
              <>
                <div className="upload-icon-audio">
                <Loading theme="outline" size={20} fill="#2563EB" strokeWidth={3} />
                </div>
                <div className="upload-text-audio">上传中... {uploadProgress}%</div>
                <div className="upload-progress-bar">
                  <div
                    className="upload-progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="upload-icon-audio">
                  <Music theme="outline" size={20} fill="#333" strokeWidth={3} />
                </div>
                <div className="upload-text-audio">点击上传音频文件</div>
                <div className="upload-hint">支持MP3、WAV、AAC，最大20MB</div>
              </>
            )}
          </label>
        </div>
      )}

      <div className="tts-generate">
        {/* 发音人选择 */}
        <div className="tts-row voice-selector-row">
          <label>发音人</label>
          <div className="voice-selector-wrapper">
            <button
              type="button"
              className="voice-selector-btn"
              onClick={() => setShowVoiceSelector(!showVoiceSelector)}
              disabled={loadingSpeakers}
            >
              <span className="voice-selector-text">
                {loadingSpeakers ? '加载中...' : (currentSpeaker?.name || '选择发音人')}
              </span>
              <Down theme="outline" size={14} fill="#666" strokeWidth={3} />
            </button>
            {showVoiceSelector && speakers.length > 0 && (
              <div className="voice-selector-dropdown">
                {speakers.map(speaker => (
                  <div
                    key={speaker.code}
                    className={`voice-option ${selectedVoice === speaker.code ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedVoice(speaker.code);
                      setShowVoiceSelector(false);
                    }}
                  >
                    <span className="voice-option-name">{speaker.name}</span>
                    <span className="voice-option-desc">{speaker.description}</span>
                    {speaker.gender && (
                      <span className={`voice-option-gender ${speaker.gender}`}>
                        {speaker.gender === 'female' ? '女' : '男'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 合成文本输入 */}
        <div className="tts-row">
          <label htmlFor="tts-text">合成文本</label>
          <input
            id="tts-text"
            type="text"
            value={ttsText}
            onChange={(e) => setTtsText(e.target.value)}
            placeholder="输入要转语音的文本"
            disabled={ttsLoading}
          />
          <button
            type="button"
            className="voice-generate-btn"
            onClick={handleGenerateVoice}
            disabled={ttsLoading}
            title="使用阿里云TTS生成音频并自动填充"
          >
            {ttsLoading ? '生成中...' : (<><Voice theme="outline" size={16} fill="#fff" strokeWidth={3} /> 生成语音</>)}
          </button>
        </div>
      </div>

      {/* 播放器样式与选项 */}
      <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label>圆角(px)</label>
        <input type="number" min="0" max="48" value={(data.playerStyle && data.playerStyle.radius) || 0} onChange={(e) => onUpdate({ ...data, playerStyle: { ...(data.playerStyle || {}), radius: Number(e.target.value) } })} />
        <label>边框宽(px)</label>
        <input type="number" min="0" max="12" value={(data.playerStyle && data.playerStyle.borderWidth) || 0} onChange={(e) => onUpdate({ ...data, playerStyle: { ...(data.playerStyle || {}), borderWidth: Number(e.target.value) } })} />
        <label>边框色</label>
        <input type="color" value={(data.playerStyle && data.playerStyle.borderColor) || '#e8eaed'} onChange={(e) => onUpdate({ ...data, playerStyle: { ...(data.playerStyle || {}), borderColor: e.target.value } })} />
        <label><input type="checkbox" checked={data.audioOptions ? data.audioOptions.controls !== false : true} onChange={(e) => onUpdate({ ...data, audioOptions: { ...(data.audioOptions || {}), controls: e.target.checked } })} /> 显示控件</label>
        <label><input type="checkbox" checked={!!(data.audioOptions && data.audioOptions.autoplay)} onChange={(e) => onUpdate({ ...data, audioOptions: { ...(data.audioOptions || {}), autoplay: e.target.checked } })} /> 自动播放</label>
        <label><input type="checkbox" checked={!!(data.audioOptions && data.audioOptions.loop)} onChange={(e) => onUpdate({ ...data, audioOptions: { ...(data.audioOptions || {}), loop: e.target.checked } })} /> 循环</label>
      </div>
    </div>
  );
}

export default AudioUploader;
